import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaPhoneAlt } from 'react-icons/fa';
import api, { getImageUrl } from '../services/api';
import { useSettings } from '../context/SettingsContext';

function ServiceDetail() {
  const { slug } = useParams();
  const { settings } = useSettings();
  const [service, setService] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/services/${slug}`)
      .then(res => setService(res.data))
      .catch(() => setService(null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    api.get('/services').then(res => setAllServices(res.data)).catch(() => {});
  }, []);

  if (loading) return <div className="container" style={{ padding: '2rem' }}>Yukleniyor...</div>;
  if (!service) return <div className="container" style={{ padding: '2rem' }}>Hizmet bulunamadi.</div>;

  const images = service.images || [];
  const ogImage = service.og_image || images[0]?.image_path;

  const closeLightbox = () => setLightboxIndex(null);
  const showPrev = (e) => { e.stopPropagation(); setLightboxIndex((i) => (i - 1 + images.length) % images.length); };
  const showNext = (e) => { e.stopPropagation(); setLightboxIndex((i) => (i + 1) % images.length); };

  return (
    <>
      <Helmet>
        <title>{service.meta_title || service.title} | {settings.site_title || ''}</title>
        {service.meta_description && <meta name="description" content={service.meta_description} />}
        <meta property="og:title" content={service.meta_title || service.title} />
        <meta property="og:description" content={service.meta_description || service.short_description || ''} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        {ogImage && <meta property="og:image" content={service.og_image || `${window.location.origin}${getImageUrl(ogImage)}`} />}
      </Helmet>

      <div className="page-header">
        <div className="container">
          <h1>{service.title}</h1>
          <div className="breadcrumb">
            <Link to="/">Ana Sayfa</Link> / <span>{service.title}</span>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="service-detail-layout">
            <div className="service-detail-content">
              {service.short_description && (
                <p className="service-detail-description">{service.short_description}</p>
              )}
              {images.length === 0 ? (
                <p style={{ color: '#888' }}>Bu hizmete henüz fotoğraf eklenmemiş.</p>
              ) : (
                <div className="gallery-grid">
                  {images.map((img, idx) => (
                    <div
                      key={img.id}
                      className="gallery-item gallery-item-animated"
                      onClick={() => setLightboxIndex(idx)}
                    >
                      <img src={getImageUrl(img.image_path)} alt={`${service.title} ${idx + 1}`} loading="lazy" />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <aside className="service-sidebar">
              <h3 className="service-sidebar-title">Hizmetlerimiz</h3>
              <ul className="service-sidebar-list">
                {allServices.map(s => (
                  <li key={s.id}>
                    <Link
                      to={`/hizmetler/${s.slug}`}
                      className={s.slug === slug ? 'active' : ''}
                    >
                      {s.title}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="service-sidebar-cta">
                <h4>Teklif Alın</h4>
                <p>Projeleriniz için hemen bize ulaşın!</p>
                {settings.phone && (
                  <a href={`tel:${settings.phone}`} className="service-sidebar-cta-btn">
                    <FaPhoneAlt /> {settings.phone}
                  </a>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>

      {lightboxIndex !== null && images[lightboxIndex] && (
        <div className="lightbox" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox}>&times;</button>
          {images.length > 1 && (
            <button className="lightbox-nav lightbox-prev" onClick={showPrev}>&#8249;</button>
          )}
          <img
            src={getImageUrl(images[lightboxIndex].image_path)}
            alt={`${service.title} ${lightboxIndex + 1}`}
            onClick={e => e.stopPropagation()}
          />
          {images.length > 1 && (
            <button className="lightbox-nav lightbox-next" onClick={showNext}>&#8250;</button>
          )}
        </div>
      )}
    </>
  );
}

export default ServiceDetail;
